import React, { useCallback, useEffect, useState } from 'react';
import { Edit, FileDownload, FileUpload } from '@mui/icons-material';
import api from '../../../../api/axios';
import PaymentMethodEditModal, { CountryConfigItem } from "../modals/PaymentMethodEditModalProps";
import PaymentMethodUploadModal from "../modals/PaymentMethodUploadModal";

export interface PaymentMethodItem {
    id: string;
    name: string;
    isActive: boolean;
}

export interface MatrixEntry {
    countryCode: string;
    countryName: string;
    providers: Record<string, any>;
}

const ALL_AVAILABLE_COUNTRY_CODES = [
    'DE', 'AT', 'CH', 'US', 'GB', 'FR', 'IT', 'ES', 'NL', 'BE', 'PL', 'SE', 'NO', 'DK', 'FI', 'IE', 'PT'
];

const PaymentMethodSettings: React.FC = () => {
    const [methods, setMethods] = useState<PaymentMethodItem[]>([]);
    const [matrix, setMatrix] = useState<MatrixEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
    const [selectedMethodForUpload, setSelectedMethodForUpload] = useState<PaymentMethodItem | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [selectedMethodForEdit, setSelectedMethodForEdit] = useState<PaymentMethodItem | null>(null);
    const [editingConfigs, setEditingConfigs] = useState<CountryConfigItem[]>([]);
    const [savingEdit, setSavingEdit] = useState<boolean>(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [methodsRes, matrixRes] = await Promise.all([
                api.get<PaymentMethodItem[]>('/system/payment-methods'),
                api.get<{ matrix: MatrixEntry[] }>('/system/payment-methods/matrix')
            ]);

            setMethods(methodsRes.data);
            setMatrix(matrixRes.data.matrix || []);
        } catch (error) {
            console.error('Failed to load payment method settings:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleToggleMethodActive = async (method: PaymentMethodItem) => {
        try {
            await api.put(`/system/payment-methods/${method.id}`, {
                isActive: !method.isActive
            });

            setMethods(prev =>
                prev.map(m => (m.id === method.id ? { ...m, isActive: !m.isActive } : m))
            );
        } catch (error) {
            console.error('Failed to toggle payment method active state:', error);
        }
    };

    const handleOpenUploadModal = (method: PaymentMethodItem) => {
        setSelectedMethodForUpload(method);
        setUploadModalOpen(true);
    };

    const handleConfirmUpload = async (file: File, mfaCode?: string) => {
        if (!selectedMethodForUpload) return;
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const headers: Record<string, string> = {};
            if (mfaCode) {
                headers['X-MFA-Code'] = mfaCode;
            }

            await api.post(`/system/payment-methods/upload/${selectedMethodForUpload.id}`, formData, {
                headers
            });

            setUploadModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to upload provider configuration:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadExcel = async (method: PaymentMethodItem) => {
        try {
            const response = await api.get(`/system/payment-methods/download/${method.id}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `provider_${method.name}_config.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download excel configuration:', error);
        }
    };

    const handleOpenEditModal = (method: PaymentMethodItem) => {
        setSelectedMethodForEdit(method);

        const currentConfigs: CountryConfigItem[] = [];
        matrix.forEach(entry => {
            const providerConfig = entry.providers[method.name];
            if (providerConfig) {
                currentConfigs.push({
                    countryCode: entry.countryCode,
                    inputCurrency: providerConfig.inputCurrency || 'EUR',
                    outputCurrency: providerConfig.outputCurrency || 'EUR',
                    fixedMinor: providerConfig.fixedMinor || 0,
                    feeBps: providerConfig.feeBps || 0,
                    fxFeeBps: providerConfig.fxFeeBps || 0,
                    minAmountMinor: providerConfig.minAmountMinor || 0,
                    maxAmountMinor: providerConfig.maxAmountMinor || 100000,
                    kycRequired: !!providerConfig.kycRequired,
                    isActive: !!providerConfig.enabled,
                });
            }
        });

        setEditingConfigs(currentConfigs);
        setEditModalOpen(true);
    };

    const handleSaveEditConfigs = async (methodId: string, configs: CountryConfigItem[]) => {
        setSavingEdit(true);
        try {
            for (const cfg of configs) {
                await api.post(`/system/payment-methods/country/${methodId}`, cfg);
            }

            setEditModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to save country configurations:', error);
        } finally {
            setSavingEdit(false);
        }
    };

    const getCountryCountForMethod = (methodName: string): number => {
        return matrix.filter(entry => entry.providers && entry.providers[methodName] !== undefined).length;
    };

    return (
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Payment Provider Settings</h2>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Manage payment methods, country restrictions, fee structures, and bulk configurations.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm text-neutral-500 animate-pulse">
                    Loading payment configurations...
                </div>
            ) : (
                <div className="overflow-x-auto border border-neutral-200 dark:border-neutral-800 rounded-lg">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-neutral-50 dark:bg-neutral-800/60 text-neutral-600 dark:text-neutral-400 font-semibold uppercase text-xs tracking-wider border-b border-neutral-200 dark:border-neutral-800">
                        <tr>
                            <th className="py-3 px-4">Name</th>
                            <th className="py-3 px-4">Status</th>
                            <th className="py-3 px-4">Anzahl Länder</th>
                            <th className="py-3 px-4 text-right">Aktionen</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                        {methods.map(method => {
                            const countryCount = getCountryCountForMethod(method.name);
                            return (
                                <tr key={method.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-colors">
                                    <td className="py-3.5 px-4 font-bold text-gray-900 dark:text-white">
                                        {method.name}
                                    </td>
                                    <td className="py-3.5 px-4">
                                        <button
                                            type="button"
                                            onClick={() => handleToggleMethodActive(method)}
                                            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                                                method.isActive
                                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400'
                                                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
                                            }`}
                                        >
                                            {method.isActive ? 'Aktiv' : 'Inaktiv'}
                                        </button>
                                    </td>
                                    <td className="py-3.5 px-4 text-neutral-700 dark:text-neutral-300 font-medium">
                                        {countryCount} {countryCount === 1 ? 'Land' : 'Länder'}
                                    </td>
                                    <td className="py-3.5 px-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleOpenEditModal(method)}
                                                className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded transition-colors"
                                                title="Bearbeiten"
                                            >
                                                <Edit fontSize="small" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleOpenUploadModal(method)}
                                                className="p-1.5 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/50 rounded transition-colors"
                                                title="Excel Hochladen"
                                            >
                                                <FileUpload fontSize="small" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDownloadExcel(method)}
                                                className="p-1.5 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 rounded transition-colors"
                                                title="Excel Herunterladen"
                                            >
                                                <FileDownload fontSize="small" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}

            <PaymentMethodUploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onConfirm={handleConfirmUpload}
                methodId={selectedMethodForUpload?.id || null}
                methodName={selectedMethodForUpload?.name || null}
                loading={uploading}
            />

            <PaymentMethodEditModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSave={handleSaveEditConfigs}
                methodId={selectedMethodForEdit?.id || null}
                methodName={selectedMethodForEdit?.name || null}
                initialConfigs={editingConfigs}
                availableCountryCodes={ALL_AVAILABLE_COUNTRY_CODES}
                loading={savingEdit}
            />
        </div>
    );
};

export default PaymentMethodSettings;